/**
 * AvatarLightbox - Componente reutilizável para exibir avatar com lightbox.
 *
 * Ao clicar na foto, abre em tela cheia com overlay escuro.
 * Clica fora ou no X para fechar. Igual ao WhatsApp.
 *
 * Tratamento de erro:
 * Se a URL da imagem falhar (ex: URL temporária do WhatsApp expirada),
 * o componente troca automaticamente para o `fallback` recebido por prop.
 * Quando `src` muda (ex: nova mensagem inbound atualizou avatar_url),
 * o estado de erro é resetado para tentar carregar a nova URL.
 */
import React, { useState, useCallback, useEffect } from 'react';

const AvatarLightbox = ({ src, alt, fallback, className, size }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  // Resetar erro quando src muda — permite recuperação automática
  // quando o backend atualiza avatar_url via webhook de mensagem inbound.
  useEffect(() => {
    setHasImageError(false);
  }, [src]);

  // src considerada utilizável: existe E ainda não falhou
  const isImageUsable = Boolean(src) && !hasImageError;

  const open = useCallback((e) => {
    if (isImageUsable) {
      e.stopPropagation();
      setIsOpen(true);
    }
  }, [isImageUsable]);

  const close = useCallback(() => setIsOpen(false), []);

  const handleImageError = useCallback(() => {
    setHasImageError(true);
  }, []);

  // Fechar com ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  // Bloquear scroll quando aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={className || 'avatar'}
        onClick={open}
        style={isImageUsable ? { cursor: 'pointer' } : undefined}
      >
        {isImageUsable ? (
          <img
            src={src}
            alt={alt || ''}
            style={imgStyle(size)}
            onError={handleImageError}
          />
        ) : (
          fallback
        )}
      </div>

      {isOpen && (
        <div style={overlayStyle} onClick={close}>
          {/* Botão fechar */}
          <button style={closeBtnStyle} onClick={close}>&times;</button>

          {/* Imagem expandida */}
          <img
            src={src}
            alt={alt || ''}
            style={expandedImgStyle}
            onClick={(e) => e.stopPropagation()}
            onError={handleImageError}
          />
        </div>
      )}
    </>
  );
};

/* ---- Estilos inline (sem dependência de CSS externo) ---- */

const imgStyle = (size) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  objectFit: 'cover',
});

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
  cursor: 'pointer',
  animation: 'fadeIn 0.2s ease',
};

const closeBtnStyle = {
  position: 'absolute',
  top: 16,
  right: 20,
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: 36,
  cursor: 'pointer',
  zIndex: 100000,
  lineHeight: 1,
  padding: '8px',
  opacity: 0.8,
};

const expandedImgStyle = {
  maxWidth: '90vw',
  maxHeight: '85vh',
  borderRadius: 8,
  objectFit: 'contain',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  cursor: 'default',
};

export default AvatarLightbox;
